import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { message } from 'antd';

const getErrorMessageFromResponseBody = (bodyText) => {
  if (!bodyText) return 'Failed to send reset email';
  try {
    const parsed = JSON.parse(bodyText);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object') {
      const fullMessage = [parsed.error, parsed.message, parsed.details]
        .filter(Boolean)
        .join(' ')
        .trim();
      return fullMessage || JSON.stringify(parsed);
    }
    return String(parsed);
  } catch {
    return bodyText;
  }
};

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const navigate = useNavigate();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email: values.email }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(getErrorMessageFromResponseBody(errorBody));
      }

      message.success('Password reset code sent successfully!');
      setSubmittedEmail(values.email.trim().toLowerCase());
      setEmailSent(true);
      setError(null);
    } catch (error) {
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <MailOutlined className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-center mb-2">
              Check your email
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              We sent a password reset code to your email address.
            </p>
            <button
              onClick={() => navigate(`/reset-password?email=${encodeURIComponent(submittedEmail)}`)}
              className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark transition duration-300 font-bold mb-4"
            >
              Enter Reset Code
            </button>
            <p className="text-sm text-gray-500">
              Didn't receive the email? Check your spam folder or{' '}
              <button
                onClick={() => setEmailSent(false)}
                className="font-medium text-primary hover:text-primary-dark"
              >
                try again
              </button>
            </p>
          </div>
          <div className="mt-6">
            <Link
              to="/login"
              className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowLeftOutlined className="mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-screen">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <div>
          <h2 className="text-2xl font-bold text-center">Forgot your password?</h2>
          <p className="mt-2 text-sm text-gray-600 text-center">
            Enter your email and we will send a reset code.
          </p>
        </div>
        
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        <form onSubmit={(e) => { e.preventDefault(); onFinish({ email: document.getElementById('email').value }); }} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter your email address"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark transition duration-300 font-bold disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate('/reset-password')}
          className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition duration-300 font-medium"
        >
          I already have a reset code
        </button>

        <div className="text-center">
          <Link
            to="/login"
            className="font-medium text-primary hover:text-primary-dark flex items-center justify-center"
          >
            <ArrowLeftOutlined className="mr-1" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
